import { axiosInstance } from "@/lib/axios";
import queryClient from "@/lib/queryClient";
import { productSchema, ProductType, productUpdateSchema, ProductUpdateType } from "@/schemas/product-schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";
import { getCategoriesFn } from "./category";

export const ProductQuerySchema = z.object({
  categoryId: z.number().optional(),
  includeArchived: z.boolean().optional(),
  limit: z.number().min(1).max(100).optional(),
  offset: z.number().min(0).optional(),
});

export type ProductCreateType = z.infer<typeof productSchema> & { partnerIds?: string[] };
export type ProductQueryType = z.infer<typeof ProductQuerySchema>;

// API functions
export async function getProductsFn(query: ProductQueryType = {}) {
  const params = new URLSearchParams();

  if (query.categoryId)
    params.append("categoryId", query.categoryId.toString());
  if (query.includeArchived !== undefined)
    params.append("includeArchived", query.includeArchived.toString());
  if (query.limit) params.append("limit", query.limit.toString());
  if (query.offset) params.append("offset", query.offset.toString());

  const url = `/api/products${
    params.toString() ? `?${params.toString()}` : ""
  }`;
  return (
    await axiosInstance.get<{
      data: ProductType[];
      pagination: {
        total: number;
        limit: number;
        offset: number;
        hasMore: boolean;
      };
    }>(url)
  ).data;
}

export async function getProductByIdFn(id: string) {
  return (await axiosInstance.get<ProductType>(`/api/products/${id}`)).data;
}

export async function createProductFn(data: ProductCreateType) {
  return (await axiosInstance.post<ProductType>("/api/products", data)).data;
}

export async function updateProductFn(data: ProductUpdateType) {
  const { id, ...updateData } = data;
  return (await axiosInstance.put(`/api/products/${id}`, updateData)).data;
}

export async function deleteProductFn(id: string) {
  return (await axiosInstance.delete(`/api/products/${id}`)).data;
}

export async function toggleProductArchiveFn(id: string, isArchived: boolean) {
  return (await axiosInstance.patch(`/api/products/${id}`, { isArchived }))
    .data;
}

export async function getProductPartnersFn(productId: string) {
  return (
    await axiosInstance.get<{ success: boolean; data: any[] }>(
      `/api/products/${productId}/partners`,
    )
  ).data.data;
}

export const getNavigationProducts = async () => {
  try {
    const categories = (await getCategoriesFn())?.data;
    //how many products should we show in the navigation 🤔?
    const products = (await getProductsFn({ limit: 100 })).data.map(
      (product) => {
        const category = categories.find(
          (category) => category.id === product.categoryId
        );
        return {
          ...product,
          category: {
            name: category?.name,
            id: category?.id,
            createdAt: category?.createdAt,
          },
        };
      }
    );
    return products;
  } catch (err) {
    console.error("err", err);
    return [];
  }
};

const Product = {
  Get: {
    useQuery: (query: ProductQueryType = {}, options = {}) =>
      useQuery({
        queryKey: ["Products", query],
        queryFn: () => getProductsFn(query),
        ...options,
      }),
  },

  GetById: {
    useQuery: (id: string, options = {}) =>
      useQuery({
        queryKey: ["Product", id],
        queryFn: () => getProductByIdFn(id),
        enabled: !!id,
        ...options,
      }),
  },

  Create: {
    useMutation: (options = {}) =>
      useMutation({
        mutationFn: createProductFn,
        onSuccess: (data) => {
          toast.success(`Product "${data.name}" created successfully`);
          queryClient.invalidateQueries({ queryKey: ["Products"] });
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.error || "Failed to create product";
          toast.error(message);
        },
        ...options,
      }),
  },

  Update: {
    useMutation: (options = {}) =>
      useMutation({
        mutationFn: updateProductFn,
        onSuccess: (data) => {
          toast.success(`Product "${data.name}" updated successfully`);
          queryClient.invalidateQueries({ queryKey: ["Products"] });
          queryClient.invalidateQueries({ queryKey: ["Product", data.id] });
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.error || "Failed to update product";
          toast.error(message);
        },
        ...options,
      }),
  },

  Delete: {
    useMutation: (options = {}) =>
      useMutation({
        mutationFn: deleteProductFn,
        onSuccess: () => {
          toast.success("Product deleted successfully");
          queryClient.invalidateQueries({ queryKey: ["Products"] });
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.error || "Failed to delete product";
          toast.error(message);
        },
        ...options,
      }),
  },

  NavigationProducts: {
    useQuery: (options = {}) => {
      return useQuery({
        queryKey: ["navigation-products"],
        queryFn: () => getNavigationProducts(),
        ...options,
      });
    },
  },

  ToggleArchive: {
    useMutation: (options = {}) =>
      useMutation({
        mutationFn: ({ id, isArchived }: { id: string; isArchived: boolean }) =>
          toggleProductArchiveFn(id, isArchived),
        onSuccess: (data, variables) => {
          const status = variables.isArchived ? "archived" : "unarchived";
          toast.success(`Product ${status} successfully`);
          queryClient.invalidateQueries({ queryKey: ["Products"] });
          queryClient.invalidateQueries({
            queryKey: ["Product", variables.id],
          });
        },
        onError: (error: any) => {
          const message =
            error?.response?.data?.error || "Failed to update product status";
          toast.error(message);
        },
        ...options,
      }),
  },

  GetPartners: {
    useQuery: (productId: string, options = {}) =>
      useQuery({
        queryKey: ["Product", productId, "partners"],
        queryFn: () => getProductPartnersFn(productId),
        enabled: !!productId,
        ...options,
      }),
  },
};

export default Product;
